import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import BankCard from '../../../common/BankCard';
import { getProviderBalance, getUserWalletDetailsById, withdrawFromWallet, withdrawFromWalletOtp } from '../../../actions/wallet';
import { closeModalAction } from '../../../actions/modal';
import { wallet as walletTypes } from '../../../types';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import { useForm } from 'react-hook-form';
import { convertIntoPaisa, convertIntoRupee, PaymentProvider } from '../../../utils/utils';
import { useCallback } from 'react';
import { convertToLakhOrCrore, isBoltOrg, ScogoID } from '../../../utils/common';
import { postBankDetails } from '../../../actions/users';
import config from '../../../config';
import development from '../../../config/env/development';
import Otp from '../../../components/Inputs/Otp';

const formName = 'sendToBank';

export default function SendToBank({ isShowDailyLimit = true }) {
    const dispatch = useDispatch();
    const withdrawFromWalletOtpSent = useSelector((state) => state.wallet.withdrawFromWalletOtpSent);
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const defaultValues = { amount: '', otp: '' };
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const myWalletDetail = newLoggedUserData.wallet;
    const [amountError, setAmountError] = useState('');
    const [showAddBankForm, setShowAddBankForm] = useState(false);

    const methods = useForm({ defaultValues, mode: 'all' });

    useEffect(() => {
        if (development.name === config.name) {
            methods.setValue('bank', 'HDFC Bank');
            methods.setValue('acc_num', '1121431121541121');
            methods.setValue('ifsc', 'HDFC0001234');
            methods.setValue('holder_name', 'John Doe');
            methods.setValue('upi_vpa', 'test@upi');
        }
        if (newLoggedUserData.bank && Object.keys(newLoggedUserData.bank).length > 0) {
            setShowAddBankForm(false);
        }
        return () => dispatch({ type: walletTypes.WITHDRAW_FROM_WALLET_OTP_SUCCESS, payload: false });
    }, [dispatch, newLoggedUserData.bank, methods]);

    // For Bolt org users, default the Account Holder Name to the user's display name
    useEffect(() => {
        if (newLoggedUserData?.org?.id === isBoltOrg && newLoggedUserData?.user?.full_name) {
            methods.setValue('holder_name', newLoggedUserData.user.full_name);
        }
    }, [newLoggedUserData?.org?.id, newLoggedUserData?.user?.full_name, methods]);

    // For Bolt org users, the Account Holder Name must match the user's display name
    const validateHolderName = (value) => {
        const requiredName = newLoggedUserData?.user?.full_name;
        if (newLoggedUserData?.org?.id === isBoltOrg && requiredName && value !== requiredName) {
            return `Account Holder Name must be ${requiredName}`;
        }
        return true;
    };

    const handleFormSubmit = (formValues) => {

        if (showAddBankForm) {
            dispatch(postBankDetails({
                userId: newLoggedUserData.user.id, bank_details: {
                    bank: formValues.bank,
                    acc_num: formValues.acc_num,
                    upi_vpa: formValues.upi_vpa,
                    ifsc: formValues.ifsc,
                    holder_name: formValues.holder_name
                },
                callUserDetails: false,
                closeModal: false,
                callMe: true,
                formName
            }));
            return;
        }


        const amount = parseInt(formValues.amount);

        if (amount <= 0) {
            setAmountError('Amount must be greater than 0');
            return;
        }

        if (amount > myWalletDetail?.balance) {
            setAmountError('Amount exceeds available balance');
            return;
        }

        if (isShowDailyLimit && myWalletDetail?.withdraw_limit && amount > myWalletDetail.withdraw_limit) {
            setAmountError(`Amount exceeds daily withdrawal limit of ₹${myWalletDetail.withdraw_limit}`);
            return;
        }

        setAmountError('');

        if (!withdrawFromWalletOtpSent) {
            dispatch(withdrawFromWalletOtp({ formName }));
        } else {
            const payload = {
                beneficiary: newLoggedUserData?.user?.id,
                amount: convertIntoPaisa(amount),
                currency: "INR",
                otp: formValues.otp,
                withdraw_to: 'account_number',
                provider: PaymentProvider.RAZORPAY,
                remarks: ''
            };
            dispatch(withdrawFromWallet({
                data: payload,
                formName,
                cleanUp: true,
                callGetMe: true
            }));
        }
    };

    const otpCustomValidation = (otp) => {
        return otp && otp.toString().length === 6;
    };

    const { watch } = methods;
    const currentAmount = watch('amount');
    const isSubmitDisabled =
        (withdrawFromWalletOtpSent && !otpCustomValidation(watch('otp'))) ||
        !currentAmount ||
        amountError;


    const memoizedBankDetails = useMemo(() => {
        if (newLoggedUserData?.bank) {
            return {
                bank_name: newLoggedUserData?.bank?.bank_name,
                ifsc_code: newLoggedUserData?.bank?.ifsc,
                account_number: newLoggedUserData?.bank?.acc_num,
                account_holder_name: newLoggedUserData?.bank?.holder_name,
                upi_id: newLoggedUserData?.bank?.upi_vpa
            };
        }
        return {};
    }, [newLoggedUserData?.bank]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col h-full"
        >
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800 mb-2">Withdraw Amount</h2>
                <motion.div
                    className="flex flex-wrap gap-2"
                    initial={{ y: -10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                >
                    <motion.div
                        className="bg-white py-1 text-font11 px-3 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 border border-blue-100"
                        whileHover={{ scale: 1.02 }}
                    >
                        <span className="font-medium text-gray-700">User Wallet Balance:</span>
                        <span className="text-blue-600 font-semibold ml-1">₹ {myWalletDetail?.balance ? convertToLakhOrCrore(convertIntoRupee(myWalletDetail.balance)) : 0}</span>
                    </motion.div>
                    <motion.div
                        className="bg-white py-1 text-font11 px-3 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 border border-blue-100"
                        whileHover={{ scale: 1.02 }}
                    >
                        <span className="font-medium text-gray-700">Total Withdrawn:</span>
                        <span className="text-blue-600 font-semibold ml-1">₹ {myWalletDetail?.total_withdrawn ? convertToLakhOrCrore(convertIntoRupee(myWalletDetail.total_withdrawn)) : 0}</span>
                    </motion.div>
                </motion.div>
            </div>
            <Form
                methods={methods}
                onSubmit={handleFormSubmit}
                submitButtonText={showAddBankForm ? 'Add Bank Account' : withdrawFromWalletOtpSent ? 'Withdraw' : 'Request OTP'}
                onCancel={() => dispatch(closeModalAction())}
                showSubmit
                submitContainerClassName="flex gap-2 justify-end py-3 px-4 bg-gray-50 border-t border-gray-100 sticky bottom-0 z-10"
                isLoading={formButtonLoading[formName]}
                isSubmitDisabled={isSubmitDisabled && !showAddBankForm}
                submitButtonClassName="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-md transition-colors duration-200 text-sm"
                cancelButtonClassName="border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-md transition-colors duration-200 text-sm"
            >
                <div className="overflow-y-auto max-h-[calc(100vh-220px)]">
                    <motion.div
                        className="px-4 py-3"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <RenderBankDetails bankDetails={memoizedBankDetails} />
                    </motion.div>

                    {(!newLoggedUserData?.bank || Object.keys(newLoggedUserData?.bank).length === 0) && !showAddBankForm ? (
                        <motion.div
                            className="flex flex-col items-center justify-center py-6"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <span className="material-icons text-blue-500 text-4xl mb-3">account_balance</span>
                            <p className="text-font14 font-medium text-gray-700 mb-4">Bank account details are required for withdrawal</p>
                            <button
                                type="button"
                                onClick={() => setShowAddBankForm(true)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors duration-200 text-sm flex items-center"
                            >
                                <span className="material-icons text-font16 mr-1">add</span>
                                Add Bank Account
                            </button>
                        </motion.div>
                    ) : showAddBankForm ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-3">
                            <Input
                                label="Bank Name"
                                name="bank"
                                placeholder="Enter bank name"
                                required
                                className="w-full"
                            />
                            <Input
                                label="Account Number"
                                name="acc_num"
                                placeholder="Enter account number"
                                required
                                className="w-full"
                            />
                            <Input
                                label="UPI VPA (Optional)"
                                name="upi_vpa"
                                placeholder="Enter UPI ID"
                                className="w-full"
                            />
                            <Input
                                label="IFSC Code"
                                name="ifsc"
                                placeholder="Enter IFSC code"
                                required
                                className="w-full"
                            />
                            <Input
                                label="Account Holder Name"
                                name="holder_name"
                                placeholder="Enter account holder name"
                                required
                                validate={validateHolderName}
                                className="w-full"
                            />
                        </div>
                    ) :
                        <motion.div
                            className="px-4 pb-4 relative"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <div className='flex gap-4'>
                                <div className="mb-3">
                                    <Input
                                        allowFloats
                                        className="w-full"
                                        label="Amount"
                                        type={"number"}
                                        name="amount"
                                        placeholder={"Enter amount to withdraw"}
                                        required
                                        readOnly={withdrawFromWalletOtpSent}
                                        onChange={() => setAmountError('')}
                                        inputClassName="border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 rounded-md text-sm"
                                        labelClassName="text-gray-700 font-medium mb-1 block text-sm"
                                        validate={(value) => {
                                            if (value === '') return true;
                                            return (value > 0 && value <= myWalletDetail?.balance && value >= 400) ? true : 'Amount must be greater than 400 and less than or equal to your wallet balance';
                                        }}
                                    />
                                </div>

                                {amountError && (
                                    <motion.p
                                        className="text-red-500 text-font11 -mt-2 mb-3"
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                    >
                                        {amountError}
                                    </motion.p>
                                )}


                                {withdrawFromWalletOtpSent && <AnimatePresence><div className="mb-3">
                                    <Otp name='otp' />
                                    {/* <Input
                                        className="w-full"
                                        label="OTP"
                                        type={"text"}
                                        name="otp"
                                        placeholder={"Enter 6-digit OTP"}
                                        required
                                        inputClassName="border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 rounded-md text-sm"
                                        labelClassName="text-gray-700 font-medium mb-1 block text-sm"
                                        validate={(otp) => {
                                            if (otp === '') return true;
                                            return (otpCustomValidation(otp) || 'Please enter a valid 6-digit OTP');
                                        }}
                                    /> */}

                                </div></AnimatePresence>}
                            </div>
                            {withdrawFromWalletOtpSent && (
                                <div className="flex items-center text-green-600 text-font11 mt-1">
                                    <span className="material-icons text-font12 mr-1">check_circle</span>
                                    OTP has been sent to your registered mobile number
                                </div>
                            )}

                            {isShowDailyLimit && (
                                <motion.div
                                    className="flex items-center text-slate-700 text-font11 mt-2 bg-yellow-50 p-1.5 rounded-md border border-yellow-100"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.4 }}
                                >
                                    <span className="material-icons text-yellow-500 text-font12 mr-1">info</span>
                                    <p>Your daily withdrawal limit is <span className="font-semibold">₹ {convertIntoRupee(myWalletDetail?.withdraw_limit)}</span></p>
                                </motion.div>
                            )}
                        </motion.div>}
                </div>
            </Form>
        </motion.div>
    );
}

export const ScogoAmountBalance = () => {
    const dispatch = useDispatch();
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const providerBalance = useSelector((state) => state.wallet.providerBalance);
    const providerBalanceLoading = useSelector((state) => state.loading.providerBalanceLoading);
    const userWalletById = useSelector((state) => state.wallet.userWalletById);

    const memoizedProviderBalance = useMemo(() => {
        let b = 0;
        let m = 0;
        if (newLoggedUserData.org.id === ScogoID && providerBalance.length > 0) {
            b = providerBalance.find((item) => item.category === 'PAYOUT')?.balance;
            // b = providerBalance.find((item) => item.provider === PaymentProvider.RAZORPAY)?.balance;
            m = providerBalance.find((item) => item.provider === PaymentProvider.BULKPE)?.balance;
        } else if (userWalletById && userWalletById[newLoggedUserData.user.id]) {
            b = userWalletById[newLoggedUserData.user.id].balance
        }
        return { withdrawableBalance: convertIntoRupee(b).toFixed(2), manualTransferBalance: convertIntoRupee(m).toFixed(2) };
    }, [providerBalance, newLoggedUserData.org.id, newLoggedUserData.user.id, userWalletById]);

    const getBalance = useCallback((org_id, user_id) => {
        if (org_id === ScogoID) {
            dispatch(getProviderBalance({}));
        } else {
            dispatch(getUserWalletDetailsById({ userId: user_id }));
        }
    }, [dispatch]);

    useEffect(() => {
        getBalance(newLoggedUserData.org.id, newLoggedUserData.user.id);
    }, [getBalance, newLoggedUserData.org.id, newLoggedUserData.user.id]);

    return (
        <div className='flex items-center justify-between gap-2'>
            <motion.div
                className="bg-white py-1.5 text-font11 px-3 rounded-lg flex items-center justify-between shadow-sm border border-blue-100"
                whileHover={{ scale: 1.01 }}
            >
                <span className="text-gray-700 font-medium">{newLoggedUserData.org.id === ScogoID ? "Scogo Balance" : "Balance"}: <span className="text-blue-600 font-semibold">₹ {memoizedProviderBalance.withdrawableBalance}</span></span>
                <motion.span
                    className={`material-icons text-font12 bg-gray-100 hover:bg-blue-100 hover:text-blue-600 cursor-pointer p-0.5 rounded-full ml-2 transition-colors duration-200 ${providerBalanceLoading ? 'animate-spin text-blue-600' : 'text-gray-600'}`}
                    onClick={() => getBalance(newLoggedUserData.org.id, newLoggedUserData.user.id)}
                    whileHover={{ rotate: 180 }}
                    transition={{ duration: 0.3 }}
                >
                    refresh
                </motion.span>
            </motion.div>
            {/* {newLoggedUserData.org.id === ScogoID ? <motion.div
                className="bg-white py-1.5 text-font11 px-3 rounded-lg flex items-center justify-between shadow-sm border border-blue-100"
                whileHover={{ scale: 1.01 }}
            >
                <span className="text-gray-700 font-medium">Manual Transfer Balance: <span className="text-blue-600 font-semibold">₹ {memoizedProviderBalance.manualTransferBalance}</span></span>
                <motion.span
                    className={`material-icons text-font12 bg-gray-100 hover:bg-blue-100 hover:text-blue-600 cursor-pointer p-0.5 rounded-full ml-2 transition-colors duration-200 ${providerBalanceLoading ? 'animate-spin text-blue-600' : 'text-gray-600'}`}
                    onClick={() => getBalance(newLoggedUserData.org.id, newLoggedUserData.user.id)}
                    whileHover={{ rotate: 180 }}
                    transition={{ duration: 0.3 }}
                >
                    refresh
                </motion.span>
            </motion.div> : null} */}-
        </div>
    );
}

export const RenderBankDetails = ({ bankDetails }) => {
    if (bankDetails && Object.keys(bankDetails).length === 0) return null;

    const getBankDetailsCard = () => {
        const bankAccountNumber = bankDetails?.account_number;
        const bank_name = bankDetails?.bank_name;
        const bank_ifsc_code = bankDetails?.ifsc_code;
        const bank_account_holder_name = bankDetails?.account_holder_name;
        const upi_id = bankDetails?.upi_id;

        if ((bankAccountNumber && bank_ifsc_code) || upi_id) {
            return (
                <motion.div
                    whileHover={{ scale: 1.01 }}
                    transition={{ duration: 0.2 }}
                    className="rounded-lg overflow-hidden shadow-sm"
                >
                    <BankCard
                        bankName={bank_name}
                        ifscCode={bank_ifsc_code}
                        bankAccountHolderName={bank_account_holder_name}
                        bankAccountNumber={bankAccountNumber}
                        upi_id={upi_id}
                    />
                </motion.div>
            );
        }

        return (
            <motion.div
                className="px-3 border-2 border-red-200 bg-red-50 py-3 rounded-lg text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
            >
                <span className="material-icons text-red-500 text-2xl mb-1">account_balance</span>
                <p className="text-font13 font-medium text-red-600">Bank Details Not Found</p>
                <p className='text-font11 text-red-400 mt-0.5'>Please update bank details to proceed with withdrawals</p>
            </motion.div>
        );
    };

    return (
        <>
            <motion.div
                className='pt-1 px-1'
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <p className='text-font11 font-medium md:mr-0 mr-1'>
                    Amount will be credited to the following bank details
                </p>
            </motion.div>
            <div>{getBankDetailsCard()}</div>
        </>
    );
};